"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { themes, type Theme } from "./themes"

interface ThemeContextType {
    theme: Theme
    setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export function useTheme() {
    const context = useContext(ThemeContext)
    if (!context) {
        throw new Error("useTheme must be used within ThemeProvider")
    }
    return context
}

interface ThemeProviderProps {
    children: ReactNode
    estabelecimentoTheme?: string | null
}

const THEME_CLASSES = ["theme-default", "theme-modern", "theme-elegant", "theme-vibrant", "theme-minimal"]

export function ThemeProvider({ children, estabelecimentoTheme }: ThemeProviderProps) {
    const [theme, setThemeState] = useState<Theme>("default")

    useEffect(() => {
        let resolved: Theme = "default"

        // Priority 1: Use estabelecimento.theme if available
        if (estabelecimentoTheme && estabelecimentoTheme in themes) {
            resolved = estabelecimentoTheme as Theme
        } else if (typeof window !== "undefined") {
            // Priority 2: Check URL params for theme (for testing)
            const params = new URLSearchParams(window.location.search)
            const urlTheme = params.get("theme") as Theme | null
            if (urlTheme && urlTheme in themes) {
                resolved = urlTheme
            }
        }

        setThemeState(resolved)
    }, [estabelecimentoTheme])

    useEffect(() => {
        // Apply theme class to document root (all CSS tokens live in .theme-* classes)
        if (typeof window !== "undefined") {
            const root = document.documentElement
            root.classList.remove(...THEME_CLASSES)
            root.classList.add(`theme-${theme}`)
        }
    }, [theme])

    const setTheme = (newTheme: Theme) => {
        setThemeState(newTheme)
    }

    return (
        <ThemeContext.Provider value={{ theme, setTheme }}>
            {children}
        </ThemeContext.Provider>
    )
}